import React, { useEffect, useState } from "react";
import completedIcon from "../assets/images/completed1.svg"
import pendingIcon from "../assets/images/pending1.svg"
import approvalIcon from "../assets/images/approval.svg"
import activeIcon from "../assets/images/active.svg"
import tagIcon from "../assets/images/tagIcon.svg"
import tagIcon1 from "../assets/images/tagIcon1.svg"
import {
    Box,
    Card,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Chip,
    IconButton,
    Stack,
    Pagination,
    CircularProgress,
} from "@mui/material";

import {ChevronRight} from "@mui/icons-material";
import Api from "../state/Api";
import Utils from "../util/Utils";
import BookingsApi from "../state/bookings-api";
import { AgmodalStore, contractsStore, loginStore } from "../state/CommonState";
import { taskStore } from "./task-store";
import TaskContainer from "./task-container";

const stats = [
    {
        title: "Tasks Pending",
        value: 12,
        icon: <img src={pendingIcon} alt="Pending" height="18" width="18" />,
        bg: "#EEF6FF",
        color: "#3B82F6",
    },
    {
        title: "Awaiting Approval",
        value: 1,
        icon: <img src={approvalIcon} alt="Approval" height="18" width="18" />,
        bg: "#FEF3C7",
        color: "#F59E0B",
    },
    {
        title: "Completed Today",
        value: 3,
        icon: <img src={completedIcon} alt="Completed" height="18" width="18" />,
        bg: "#D1FAE5",
        color: "#10B981",
    },
    {
        title: "Active Shipments",
        value: 5,
        icon: <img src={activeIcon} alt="Active" height="18" width="18" />,
        bg: "#F3E8FF",
        color: "#8B5CF6",
    },
];

const tabs = [
    ["Master List"],
    ["Group by Task"],
    ["Group by Booking"]
];

const StatCard = ({ title, value, icon, bg, color }) => {
    return (
        <Card
            elevation={0}
            sx={{
                height: 94,
                border: "1px solid #E5E7EB",
                borderRadius: "11px",
                px: 1.8,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                boxShadow: "0 1px 2px rgba(0,0,0,0.02)",
            }}
        >
            <Box>
                <Typography
                    sx={{
                        fontSize: 13,
                        color: "#374151",
                        fontWeight: 500,
                    }}
                >
                    {title}
                </Typography>

                <Typography
                    sx={{
                        fontSize: 34,
                        lineHeight: 1,
                        fontWeight: 700,
                        color: "#111827",
                        mt: 0.7,
                    }}
                >
                    {value}
                </Typography>
            </Box>

            <Box
                sx={{
                    width: 32,
                    height: 32,
                    borderRadius: "8px",
                    backgroundColor: bg,
                    color,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    "& svg": {
                        fontSize: 18,
                    },
                }}
            >
                {icon}
            </Box>
        </Card>
    );
};

const TaskItem = ({
    title,
    subtitle,
    icon,
    iconBg,
    iconColor,
    due,
    priority,
    priorityBg,
    priorityColor,
    jobId,
    tags = [],
    onClick,
    isCritical = false,
    bookingId,
    actionData,
    tab,
    role
}) => {
    if (taskStore.myActionVisible) return <TaskContainer />
    return (<>
        <Card
            elevation={0}
            onClick={onClick}
            sx={{
                border: "1px solid #E5E7EB",
                borderRadius: "10px",
                display: "flex",
                alignItems: "center",
                overflow: "hidden",
                minHeight: 56,
                boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
                cursor: "pointer",
                "&:hover": {
                    boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                },
            }}
        >
            {/* Left Icon — Square for Critical, Round for Others */}
            <Box
                sx={{
                    width: 50,
                    alignSelf: "stretch",
                    flexShrink: 0,
                    backgroundColor: isCritical ? iconBg : "transparent",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    pl: isCritical ? 0 : 1.5,
                }}
            >
                {isCritical ? (
                    // Critical — square red bg icon (fills height)
                    React.cloneElement(icon, {
                        sx: { fontSize: 18, color: iconColor },
                    })
                ) : (
                    // Non-critical — round light bg icon
                    <Box
                        sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            backgroundColor: '#FFF7ED',
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                        }}
                    >
                        {React.cloneElement(icon, {
                            sx: { fontSize: 17, color:'#EA580C' },
                        })}
                    </Box>
                )}
            </Box>

            {/* Main Content */}
            <Box sx={{ flex: 1, minWidth: 0, px: 1.5, py: 1 }}>
                {/* Row 1: Title + Job ID */}
                <Stack direction="row" alignItems="center" spacing={0.8}>
                    <Typography
                        sx={{
                            fontFamily: "Figtree",
                            fontSize: 14,
                            fontWeight: 700,
                            color: "#1B2559",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {title}
                    </Typography>

                    {jobId && (
                        <Typography
                            sx={{
                                fontFamily: "Figtree",
                                fontSize: 13,
                                fontWeight: 700,
                                color: "#737D8C",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {jobId}
                        </Typography>
                    )}
                </Stack>

                {/* Row 2: Subtitle + Tags */}
                <Stack
                    direction="row"
                    alignItems="center"
                    spacing={0.8}
                    sx={{ mt: 0.4 }}
                >
                    <Typography
                        noWrap
                        sx={{
                            fontFamily: "Figtree",
                            fontSize: 12,
                            fontWeight: 400,
                            color: "#737D8C",
                        }}
                    >
                        {subtitle}
                    </Typography>

                    {tags.map((tag, index) => (
                        <Box
                            key={index}
                            sx={{
                                px: 0.8,
                                py: 0.1,
                                borderRadius: "4px",
                                backgroundColor: tag.bg || "#E5E7EB",
                                color: tag.color || "#374151",
                                fontSize: 9,
                                fontWeight: 600,
                                fontFamily: "Figtree",
                                whiteSpace: "nowrap",
                                lineHeight: 1.6,
                            }}
                        >
                            {tag.label}
                        </Box>
                    ))}
                </Stack>
            </Box>

            {/* Right: Due + Priority + Chevron */}
            <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ pr: 1.2, flexShrink: 0 }}
            >
                {due && (
                    <Chip
                        label={due}
                        size="small"
                        sx={{
                            height: 22,
                            fontFamily: "Figtree",
                            backgroundColor: "#EDF2FA",
                            color: "#63738C",
                            fontSize: 10,
                            fontWeight: 600,
                            borderRadius: "5px",
                            "& .MuiChip-label": { px: 1 },
                        }}
                    />
                )}

                {/* Priority Chip — Filled for Critical, Light for Others */}
                {priority && <Chip
                    label={priority}
                    size="small"
                    sx={{
                        height: 22,
                        fontFamily: "Figtree",
                        backgroundColor: priorityBg,
                        color: priorityColor,
                        fontSize: 10,
                        fontWeight: 700,
                        borderRadius: isCritical ? "20px" : "10px",
                        "& .MuiChip-label": { px: 1.4 },
                    }}
                />}

                <IconButton size="small" onClick={(e)=>{
                    BookingsApi.getBooking(bookingId, (data) => {
                        AgmodalStore.apiCall = false;
                        contractsStore.current = data?.contract;
                        if(tab === "Group by Task"){
                            taskStore.current = actionData?.find((action) => action._id.bookingNum === bookingId)?.Myactions;
                        }else{
                            taskStore.current = actionData?.find((action) => action._id.bookingNum === bookingId && action.Myactions.actionName === title )?.Myactions;
                        }
                        taskStore.current.creditCustomer = actionData?.find((action) => action._id.bookingNum === bookingId)?.Entities?.isCreditCustomer
                        taskStore.current.actionType = role;
                        taskStore.current.financeInformation = data?.finance;
                        taskStore.currentContainer = '';
                        taskStore.current.lastview = role;
                        // EntityV2Store.EntityRec.set(entities)
                        if (taskStore.current.isValid === undefined) taskStore.current.isValid = true;
                        if (taskStore.current.isValidfield === undefined) taskStore.current.isValidfield = [];
                        console.log(taskStore.current,"098765432");
                        
                        if (loginStore.isClient()) {
                            taskStore.myActionVisible = true;
                            taskStore.isViewAll = true;
                        } else {
                            AgmodalStore.modalPage = "TaskContainer";
                            AgmodalStore.pageVisible = true;
                        }
                    });
                }} sx={{ color: "#D1D5DB", p: 0 }}>
                    <ChevronRight sx={{ fontSize: 18 }} />
                </IconButton>
            </Stack>
        </Card>
    </>);
};

export default function Dashboardv3() {
    const [role, setRole] = useState("All");
    const [selectedTab, setSelectedTab] = useState(tabs[0][0]);
    const [selectedStatusTab, setSelectedStatusTab] = useState("Pending");
    const [data, setData] = useState([])
    const [actionData, setActionData] = useState([])
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [groupPage, setGroupPage] = useState(1);
    const [groupPageSize, setGroupPageSize] = useState(10);
    const [groupTotalCount, setGroupTotalCount] = useState(0);
    const [selectedTaskPage, setSelectedTaskPage] = useState(1);
    const [selectedTaskPageSize, setSelectedTaskPageSize] = useState(10);
    const [selectedTaskTotalCount, setSelectedTaskTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTaskGroup, setSelectedTaskGroup] = useState(null);
    const selectedTaskKey = selectedTaskGroup?.title;
    
    const priorityStatus = {
        1 :"Critical",
        2 :"High",
        3 :"Medium",
        4 :"Low",
        5 :"Least"
    }

    const getOriginDest = ({shipmentScope,origin,destination,type})=>{
        const scope = shipmentScope.split('-')
        if(scope[0] == 'Door' && type ==  'Origin' ){
            origin = Utils.extractCountry(origin)
        }
        if (scope[2] == 'Door' && type == 'Destination') {
            destination = Utils.extractCountry(destination)
        }
        return {origin ,destination}
    }

    const getShipmentType = (type)=>{
        const shipmentType = type?.includes('FCL') ? 'FCL' : type == 'LCL' ? 'LCL' : 'Air'
        return { shipmentType: shipmentType, bgColour: shipmentType === 'FCL' ? '#E8F2FF' : shipmentType === 'LCL' ? '#EDF7ED' : '#FFF2E0', color: shipmentType === 'FCL' ? '#2663C2' : shipmentType === 'LCL' ? '#218C21' : '#CC851A' }
    }

    const getDueLabel = (dueDate) => {
        const today = new Date();
        const due = new Date(dueDate);

        // Reset time to midnight for accurate day comparison
        today.setHours(0, 0, 0, 0);
        due.setHours(0, 0, 0, 0);
        const diffMs = due - today;
        const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return `Overdue by ${Math.abs(diffDays)} day${Math.abs(diffDays) > 1 ? "s" : ""}`;
        if (diffDays === 0) return "Due Today";
        if (diffDays === 1) return "Due Tomorrow";
        return dueDate ? `Due In ${diffDays} Day${diffDays > 1 ? "s" : ""}` : "";
    };
    useEffect(() => {
        const isMasterListTab = selectedTab === "Master List";
        const isGroupedTaskTab = selectedTab === "Group by Task";
        const isGroupBookingTab = selectedTab === "Group by Booking";

        if (isMasterListTab) {
            let isCurrentRequest = true;
            setIsLoading(true);
            Api.getActionList({
                actionType: role === "All" ? process.env.REACT_APP_ENTERPRISE_NAME_SENTENCECASE.toUpperCase() : role,
                displayStatus: selectedStatusTab.toUpperCase(),
                page,
                limit: pageSize,
                type: selectedTab
            }, (res) => {
                if (!isCurrentRequest) return;
                setActionData(res?.results || []);
                setData((res?.results || []).map((e) => ({
                    title: e.Myactions.actionName,
                    bookingId: e.Myactions.bookingNum,
                    subtitle: e.Bookings?.erpBookingId ? `${e.Entities.entityName} • ${e.Bookings?.erpBookingId} • ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Destination' }).destination}`
                     :`${e.Entities.entityName} • ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Destination' }).destination}`,
                    icon: <img src={priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? tagIcon1 :tagIcon} alt="Active" height="13" width="16" />,
                    iconBg: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? "#E84040" :"#FFF7ED",
                    iconColor: "#FFFFFF",
                    due: getDueLabel(e.Myactions.dueDate) || "",
                    priority: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority],
                    priorityBg: Utils.getActionPriority(e.Myactions.actionName)?.priorityBg,
                    priorityColor: Utils.getActionPriority(e.Myactions.actionName)?.priorityColor || "#FFFFFF",
                    isCritical: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? true : false,
                    tags: [
                        { label: getShipmentType(e.Myactions.shipmentType).shipmentType, bg: getShipmentType(e.Myactions.shipmentType).bgColour, color: getShipmentType(e.Myactions.shipmentType).color },
                        ...(e.Bookings?.contract?.hazardous == 'Yes' ? [{ label: "HAZ", bg: "#FEF3C7", color: "#D97706" }]:[]),
                    ],
                })));
                setTotalCount(res?.count ?? res?.totalCount ?? 0);
                setIsLoading(false);
            });

            return () => {
                isCurrentRequest = false;
            };
        }

        if (isGroupedTaskTab && !selectedTaskGroup) {
            let isCurrentRequest = true;
            setIsLoading(true);
            Api.getActionList({
                actionType: role === "All" ? process.env.REACT_APP_ENTERPRISE_NAME_SENTENCECASE.toUpperCase() : role,
                displayStatus: "PENDING",
                page: groupPage,
                limit: groupPageSize,
                type: "Group by Task"
            }, (res) => {
                if (!isCurrentRequest) return;
                setData((res?.results || []).map((e) => ({
                    title: e.actionName,
                    count: e.count,
                    icon: <img src={tagIcon} alt="Active" height="13" width="16" />
                })));
                setGroupTotalCount(res?.count ?? res?.totalCount ?? 0);
                setIsLoading(false);
            });

            return () => {
                isCurrentRequest = false;
            };
        }

        if (isGroupedTaskTab && selectedTaskGroup) {
            let isCurrentRequest = true;
            setIsLoading(true);
            Api.getActionList({
                actionType: role === "All" ? process.env.REACT_APP_ENTERPRISE_NAME_SENTENCECASE.toUpperCase() : role,
                displayStatus: "PENDING",
                keyword: selectedTaskGroup.title,
                page: selectedTaskPage,
                limit: selectedTaskPageSize,
                type: "Master List"
            }, (res) => {
                if (!isCurrentRequest) return;
                setActionData(res?.results || []);
                setSelectedTaskGroup((prev) => ({
                    ...(prev || selectedTaskGroup),
                    title: selectedTaskGroup.title,
                    count: selectedTaskGroup.count,
                    tasks: (res?.results || []).map((e) => ({
                        title: e.Myactions.actionName,
                        bookingId: e.Myactions.bookingNum,
                        subtitle: e.Bookings?.erpBookingId ? `${e.Entities.entityName} • ${e.Bookings?.erpBookingId} • ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Destination' }).destination}`
                            : `${e.Entities.entityName} • ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Destination' }).destination}`,                        
                        icon: <img src={priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? tagIcon1 : tagIcon} alt="Active" height="13" width="16" />,
                        iconBg: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? "#E84040" : "#FFF7ED",
                        iconColor: "#FFFFFF",
                        due: getDueLabel(e.Myactions.dueDate) || "",
                        priority: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority],
                        priorityBg: Utils.getActionPriority(e.Myactions.actionName)?.priorityBg,
                        priorityColor: Utils.getActionPriority(e.Myactions.actionName)?.priorityColor || "#FFFFFF",
                        isCritical: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? true : false,
                        tags: [
                            { label: getShipmentType(e.Myactions.shipmentType).shipmentType, bg: getShipmentType(e.Myactions.shipmentType).bgColour, color: getShipmentType(e.Myactions.shipmentType).color },
                            ...(e.Bookings?.contract?.hazardous == 'Yes' ? [{ label: "HAZ", bg: "#FEF3C7", color: "#D97706" }] : []),
                        ],
                    }))
                }));
                setSelectedTaskTotalCount(res?.count ?? res?.totalCount ?? selectedTaskGroup.count ?? 0);
                setIsLoading(false);
            });

            return () => {
                isCurrentRequest = false;
            };
        }

        if (isGroupBookingTab && !selectedTaskGroup) {
            let isCurrentRequest = true;
            setIsLoading(true);
            Api.getActionList({
                actionType: role === "All" ? process.env.REACT_APP_ENTERPRISE_NAME_SENTENCECASE.toUpperCase() : role,
                displayStatus: "PENDING",
                page: groupPage,
                limit: groupPageSize,
                type: "Group by Booking"
            }, (res) => {
                if (!isCurrentRequest) return;
                setData((res?.results || []).map((e) => ({
                    title: `${e?.bookingNum}`,
                    count: e.count,
                    actionName: e.actionNames,
                    subtitle: e?.Bookings?.erpBookingId ? `${e?.Entities?.entityName} • ${e?.Bookings?.erpBookingId} • ${getOriginDest({ origin: e?.Bookings?.contract?.origin, destination: e?.Bookings?.contract?.destination, shipmentScope: e?.Bookings?.contract?.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e?.Bookings?.contract?.origin, destination: e?.Bookings?.contract?.destination, shipmentScope: e?.Bookings?.contract?.shipmentScope, type: 'Destination' }).destination}` : `${e?.Entities?.entityName} • ${getOriginDest({ origin: e?.Bookings?.contract?.origin, destination: e?.Bookings?.contract?.destination, shipmentScope: e?.Bookings?.contract?.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e?.Bookings?.contract?.origin, destination: e?.Bookings?.contract?.destination, shipmentScope: e?.Bookings?.contract?.shipmentScope, type: 'Destination' }).destination}`,
                    icon: <img src={tagIcon} alt="Active" height="13" width="16" />
                })));
                setGroupTotalCount(res?.count ?? res?.totalCount ?? 0);
                setIsLoading(false);
            });

            return () => {
                isCurrentRequest = false;
            };
        }

        if (isGroupBookingTab && selectedTaskGroup) {
            let isCurrentRequest = true;
            setIsLoading(true);
            Api.getActionList({
                actionType: role === "All" ? process.env.REACT_APP_ENTERPRISE_NAME_SENTENCECASE.toUpperCase() : role,
                displayStatus: "PENDING",
                keyword: (selectedTaskGroup?.title)?.split(" ")[0],
                page: selectedTaskPage,
                limit: selectedTaskPageSize,
                type: "Master List"
            }, (res) => {
                if (!isCurrentRequest) return;
                setActionData(res?.results || []);
                setSelectedTaskGroup((prev) => ({
                    ...(prev || selectedTaskGroup),
                    title: selectedTaskGroup.title,
                    count: selectedTaskGroup.count,
                    tasks: (res?.results || []).map((e) => ({
                        title: e.Myactions.actionName,
                        bookingId: e.Myactions.bookingNum,
                        subtitle: e.Bookings?.erpBookingId ? `${e.Entities.entityName} • ${e.Bookings?.erpBookingId} • ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Destination' }).destination}`
                            : `${e.Entities.entityName} • ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Origin' }).origin} → ${getOriginDest({ origin: e.Myactions.origin, destination: e.Myactions.destination, shipmentScope: e.Myactions.shipmentScope, type: 'Destination' }).destination}`,
                        icon: <img src={priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? tagIcon1 : tagIcon} alt="Active" height="13" width="16" />,
                        iconBg: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? "#E84040" : "#FFF7ED",
                        iconColor: "#FFFFFF",
                        due: getDueLabel(e.Myactions.dueDate) || "",
                        priority: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority],
                        priorityBg: Utils.getActionPriority(e.Myactions.actionName)?.priorityBg,
                        priorityColor: Utils.getActionPriority(e.Myactions.actionName)?.priorityColor || "#FFFFFF",
                        isCritical: priorityStatus[Utils.getActionPriority(e.Myactions.actionName)?.priority] === "Critical" ? true : false,
                        tags: [
                            { label: getShipmentType(e.Myactions.shipmentType).shipmentType, bg: getShipmentType(e.Myactions.shipmentType).bgColour, color: getShipmentType(e.Myactions.shipmentType).color },
                            ...(e.Bookings?.contract?.hazardous == 'Yes' ? [{ label: "HAZ", bg: "#FEF3C7", color: "#D97706" }] : []),
                        ],
                    }))
                }));
                setSelectedTaskTotalCount(res?.count ?? res?.totalCount ?? selectedTaskGroup.count ?? 0);
                setIsLoading(false);
            });

            return () => {
                isCurrentRequest = false;
            };
        }

        return undefined;
    }, [page, pageSize, role, selectedTab, selectedStatusTab, groupPage, groupPageSize, selectedTaskPage, selectedTaskPageSize, selectedTaskKey]);

    const handleTabChange = (label) => {
        setSelectedTab(label);
        setSelectedTaskGroup(null);
        setPage(1);
    };

    const handleTabStatusChange = (label) => {
        setSelectedStatusTab(label);
        // setPage(1);
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "#FAFAFA",
                px: { xs: 2, sm: 3, md: 4,},
                py: 3,
            }}
        >
            <Box
                sx={{
                    mx: "auto",
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-end",
                        gap: 2,
                        flexDirection: { xs: "column", sm: "row"},
                    }}
                >
                    <Box>
                        <Typography
                            sx={{
                                fontSize: 25,
                                fontFamily: "figtree",
                                fontWeight: 700,
                                color: "#111827",
                            }}
                        >
                            Welcome back,
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 12,
                                fontFamily: "figtree",
                                color: "#6B7280",
                                mt: 0.4,
                            }}
                        >
                            {new Date().toLocaleDateString("en-US", {
                                weekday: "long",
                                day: "2-digit",
                                month: "long",
                                year: "numeric"
                            })} — here's what needs your attention
                            across your shipments.
                        </Typography>
                    </Box>

                    {/* Role */}
                    <Box
                        sx={{
                            width: {
                                xs: "100%",
                                sm: 215,
                            },
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: 10,
                                color: "#1F2937",
                                mb: 0.4,
                                fontWeight:500,
                                fontFamily: "figtree",
                            }}
                        >
                            Select Role
                        </Typography>

                        <FormControl fullWidth size="small">
                            <Select
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                sx={{
                                    height: 32,
                                    fontSize: 11,
                                    backgroundColor: "#fff",
                                    borderRadius: "6px",
                                    "& .MuiSelect-select": {
                                        py: 0.7,
                                    },
                                }}
                            >
                                <MenuItem value="All">All</MenuItem>
                                <MenuItem value="OPS">OPS</MenuItem>
                                <MenuItem value="CS">CS</MenuItem>
                                <MenuItem value="FINANCE">FINANCE</MenuItem>
                                <MenuItem value="SALES">SALES</MenuItem>
                                <MenuItem value="CUSTOMER">Customer</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </Box>

                {/* Stats */}
                <Box
                    sx={{
                        mt: 2,
                        display: "grid",
                        gridTemplateColumns: {
                            xs: "1fr",
                            sm: "repeat(2, 1fr)",
                            md: "repeat(4, 1fr)",
                        },
                        gap: 1.2,
                    }}
                >
                    {stats.map((item) => (
                        <StatCard
                            key={item.title}
                            title={item.title}
                            value={item.value}
                            icon={item.icon}
                            bg={item.bg}
                            color={item.color}
                        />
                    ))}
                </Box>

                {/* Tabs */}
                <div style={{display:'flex', justifyContent:'space-between'}}>
                <Box
                    sx={{
                        mt: 1.5,
                        display: "flex",
                        alignItems: "center",
                        width:'max-content',
                        gap: 1,
                        p: 0.7,
                        backgroundColor: "#FFFFFF",
                        border: "1px solid #E5E7EB",
                        borderRadius: "10px",
                        boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)",
                        overflowX: "auto",
                        "&::-webkit-scrollbar": {
                            display: "none",
                        },
                    }}
                >
                    {tabs.map(([label, count]) => {
                        const isSelected = selectedTab === label;

                        return (
                            <Box
                                key={label}
                                onClick={() => handleTabChange(label)}
                                sx={{
                                    flexShrink: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    gap: 1,
                                    px: 2,
                                    py: 0.9,
                                    borderRadius: "7px",
                                    cursor: "pointer",

                                    backgroundColor: isSelected
                                        ? "#2B3688"
                                        : "transparent",

                                    color: isSelected
                                        ? "#FFFFFF"
                                        : "#111827",

                                    transition: "all 0.2s ease",

                                    "&:hover": {
                                        backgroundColor: isSelected
                                            ? "#2B3688"
                                            : "#F5F6FA",
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: "13px",
                                        fontWeight: 500,
                                        fontFamily: "Figtree",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {label}
                                </Typography>

                                {/* <Box
                                    sx={{
                                        minWidth: 24,
                                        height: 24,
                                        px: 0.7,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",

                                        backgroundColor: isSelected
                                            ? "rgba(255,255,255,0.2)"
                                            : "#F1F2F4",

                                        color: isSelected
                                            ? "#FFFFFF"
                                            : "#6B7280",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            fontFamily: "Figtree",
                                        }}
                                    >
                                        {count}
                                    </Typography>
                                </Box> */}
                            </Box>
                        );
                    })}
                </Box>
                    <Box
                        sx={{
                            mt: 1.5,
                            display: "flex",
                            alignItems: "center",
                            width: 'max-content',
                            gap: 1,
                            p: 0.7,
                            backgroundColor: "#FFFFFF",
                            border: "1px solid #E5E7EB",
                            borderRadius: "10px",
                            boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.04)",
                            overflowX: "auto",
                            "&::-webkit-scrollbar": {
                                display: "none",
                            },
                        }}
                    >
                        {['Pending','Completed'].map((label, count) => {
                            const isSelected = selectedStatusTab === label;

                            return (
                                <Box
                                    key={label}
                                    onClick={() => handleTabStatusChange(label)}
                                    sx={{
                                        flexShrink: 0,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                        px: 2,
                                        py: 0.9,
                                        borderRadius: "7px",
                                        cursor: "pointer",

                                        backgroundColor: isSelected
                                            ? "#2B3688"
                                            : "transparent",

                                        color: isSelected
                                            ? "#FFFFFF"
                                            : "#111827",

                                        transition: "all 0.2s ease",

                                        "&:hover": {
                                            backgroundColor: isSelected
                                                ? "#2B3688"
                                                : "#F5F6FA",
                                        },
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "13px",
                                            fontWeight: 500,
                                            fontFamily: "Figtree",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        {label}
                                    </Typography>

                                    {/* <Box
                                    sx={{
                                        minWidth: 24,
                                        height: 24,
                                        px: 0.7,
                                        borderRadius: "50%",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",

                                        backgroundColor: isSelected
                                            ? "rgba(255,255,255,0.2)"
                                            : "#F1F2F4",

                                        color: isSelected
                                            ? "#FFFFFF"
                                            : "#6B7280",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            fontWeight: 500,
                                            fontFamily: "Figtree",
                                        }}
                                    >
                                        {count}
                                    </Typography>
                                </Box> */}
                                </Box>
                            );
                        })}
                    </Box>
                </div>

                {/* Task List */}
                <Box
                    sx={{
                        mt: 1,
                        p: 1.4,
                        color: "rgba(255, 255, 255, 1)",
                        boxShadow: "0px 0px 1px 0px rgba(0, 0, 0, 0.25)",
                        borderRadius: "15px",
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: "#FFFFFF",
                        height: "550px", // give the outer box a fixed/constrained height
                    }}
                >
                    <Box
                        sx={{
                            mt: 1,
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,

                            flex: 1,
                            minHeight: 0,
                            overflowY: "auto",

                            "&::-webkit-scrollbar": {
                                width: "5px",
                            },
                            "&::-webkit-scrollbar-thumb": {
                                backgroundColor: "#D1D5DB",
                                borderRadius: "10px",
                            },
                            "&::-webkit-scrollbar-track": {
                                backgroundColor: "transparent",
                            },
                        }}
                    >
                        {isLoading ? (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (selectedTab === "Group by Task" || selectedTab === "Group by Booking") && !selectedTaskGroup ? (
                            data.length > 0 ? (
                                data.map((group) => (
                                    <Card
                                        key={group.title}
                                        elevation={0}
                                        onClick={() => {
                                            setSelectedTaskPage(1);
                                            setSelectedTaskGroup({
                                                title: group.title,
                                                count: group.count,
                                                tasks: []
                                            });
                                        }}
                                        sx={{
                                            minHeight: 58,
                                            px: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            border: "1px solid #F0F1F4",
                                            borderRadius: "10px",
                                            backgroundColor: "#FAFAFB",
                                            cursor: "pointer",
                                            transition: "box-shadow 0.2s ease, transform 0.2s ease",
                                            "&:hover": {
                                                boxShadow: "0 3px 10px rgba(31, 41, 55, 0.08)",
                                                transform: "translateY(-1px)",
                                            },
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1.2}>
                                            <Box
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: "9px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#EF4444",
                                                    backgroundColor: "#FFF7ED",
                                                    "& svg": { fontSize: 19 },
                                                }}
                                            >
                                                {React.cloneElement(group.icon, { sx: { fontSize: 19 } })}
                                            </Box>
                                            <Box sx={{ flex: 1, minWidth: 0, px: 1.5, py: 1 }}>
                                                <Stack direction="row" alignItems="center" spacing={0.8}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily: "figtree",
                                                            fontSize: 14,
                                                            fontWeight: 700,
                                                            color: "#1B2559",
                                                            whiteSpace: "nowrap",
                                                        }}
                                                    >
                                                        {group.title}
                                                    </Typography>
                                                    {/* {selectedTab === "Group by Booking" && (
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "Figtree",
                                                                fontSize: 13,
                                                                fontWeight: 500,
                                                                color: "#737D8C",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            
                                                            {group.subtitle}
                                                        </Typography>
                                                    )}
                                                </Stack> */}
                                                </Stack>
                                                { selectedTab === "Group by Booking" &&  <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={0.8}
                                                    sx={{ mt: 0.4}}>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: "Figtree",
                                                                fontSize: 13,
                                                                fontWeight: 500,
                                                                color: "#737D8C",
                                                                whiteSpace: "nowrap",
                                                            }}
                                                        >
                                                            {group.subtitle}
                                                        </Typography></Stack>}

                                               {/* { selectedTab === "Group by Booking" &&  <Stack
                                                    direction="row"
                                                    alignItems="center"
                                                    spacing={0.8}
                                                    sx={{ mt: 0.4,flexWrap: "wrap", gap: 0.5 }}
                                                >
                                                    {
                                                        group.actionName?.map(e=>(
                                                            <Box
                                                                sx={{
                                                                    px: 0.8,
                                                                    py: 0.1,
                                                                    borderRadius: "4px",
                                                                    backgroundColor: "#F1F2F9",
                                                                    color: "#4A4D68",
                                                                    fontSize: 9,
                                                                    fontWeight: 500,
                                                                    fontFamily: "Figtree",
                                                                    whiteSpace: "nowrap",
                                                                    lineHeight: 1.6,
                                                                }}
                                                            >{e}</Box>
                                                        ))
                                                    }
                                                </Stack>} */}
                                                
                                            </Box>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={0.8}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                                                {group.count}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                                                {selectedTab === 'Group by Booking'?
                                                (group.count === 1 ? "task" : "tasks"):
                                                (group.count === 1 ? "booking pending" : "bookings pending")}
                                            </Typography>
                                            <ChevronRight sx={{ fontSize: 19, color: "#9CA3AF" }} />
                                        </Stack>
                                    </Card>
                                ))
                            ) : (
                                <Typography sx={{ color: "#6B7280", fontSize: 12, fontFamily: 'figtree', textAlign: "center", py: 4 }}>
                                    No pending tasks found.
                                </Typography>
                            )
                        ) : (selectedTab === "Group by Task" || selectedTab === "Group by Booking") && selectedTaskGroup ? (
                        <>
                            <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 0.5, pb: 0.4 }}>
                                <Typography
                                    onClick={() => setSelectedTaskGroup(null)}
                                    sx={{ color: "#2B3688", fontSize: 12, fontWeight: 600, fontFamily: 'figtree', cursor: "pointer" }}
                                >
                                    ← Tasks
                                </Typography>
                                <Typography sx={{ color: "#9CA3AF", fontSize: 12 }}>/</Typography>
                                <Typography sx={{ color: "#111827", fontSize: 12, fontFamily: 'figtree', fontWeight: 600 }}>
                                    {selectedTaskGroup.title}
                                </Typography>
                            </Stack>
                            <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 700, fontFamily: 'figtree', textTransform: "uppercase", letterSpacing: 0.2, px: 0.5, pb: 0.4 }}>
                                {selectedTab === "Group by Booking"
                                    ? `${selectedTaskGroup.count} ${selectedTaskGroup.count === 1 ? "task" : "tasks"} pending ${selectedTaskGroup.title}`
                                    : `${selectedTaskGroup.count} ${selectedTaskGroup.count === 1 ? "booking" : "bookings"} pending ${selectedTaskGroup.title}`
                                }
                            </Typography>
                            {selectedTaskGroup.tasks.length > 0 ? (
                                selectedTaskGroup.tasks.map((task, index) => (
                                    <TaskItem
                                        key={task.bookingId || `${task.title}-${index}`}
                                        title={selectedTab === "Group by Booking" ? task.title :`Booking ${task.bookingId || ""}`}
                                        subtitle={task.subtitle}
                                        icon={task.icon}
                                        iconBg={task.iconBg}
                                        iconColor={task.iconColor}
                                        due={task.due}
                                        priority={task.priority}
                                        priorityBg={task.priorityBg}
                                        priorityColor={task.priorityColor}
                                        tags={task.tags}
                                        isCritical={task.isCritical}
                                        bookingId={task?.bookingId}
                                        actionData={actionData}
                                        tab={selectedTab}
                                        role={role === "All" ? process.env.REACT_APP_ENTERPRISE_NAME_SENTENCECASE.toUpperCase() : role}
                                    />
                                ))
                            ) : (
                                <Typography sx={{ color: "#6B7280", fontSize: 12, textAlign: "center", py: 4 }}>
                                    No tasks found for this group.
                                </Typography>
                            )}
                            {selectedTaskTotalCount > 0 && (
                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    alignItems="center"
                                    justifyContent="space-between"
                                    spacing={1}
                                    sx={{ pt: 1.2 }}
                                >
                                    <Typography sx={{ color: "#6B7280", fontSize: 11 }}>
                                        Showing {(selectedTaskPage - 1) * selectedTaskPageSize + 1} to {Math.min(selectedTaskPage * selectedTaskPageSize, selectedTaskTotalCount)} of {selectedTaskTotalCount}
                                    </Typography>
                                    <Stack direction="row" alignItems="center" spacing={1}>
                                        <Typography sx={{ color: "#6B7280", fontSize: 11 }}>Rows</Typography>
                                        <Select
                                            size="small"
                                            value={selectedTaskPageSize}
                                            onChange={(event) => {
                                                setSelectedTaskPageSize(Number(event.target.value));
                                                setSelectedTaskPage(1);
                                            }}
                                            sx={{ height: 28, fontSize: 11, minWidth: 68 }}
                                        >
                                            {[10, 20, 50].map((size) => (
                                                <MenuItem key={size} value={size}>{size}</MenuItem>
                                            ))}
                                        </Select>
                                        <Pagination
                                            page={selectedTaskPage}
                                            count={Math.ceil(selectedTaskTotalCount / selectedTaskPageSize)}
                                            onChange={(_, nextPage) => setSelectedTaskPage(nextPage)}
                                            size="small"
                                            color="primary"
                                            siblingCount={0}
                                        />
                                    </Stack>
                                </Stack>
                            )}
                        </>
                        ) : selectedTab === "Group by ing" && !selectedTaskGroup ? (
                            data.length > 0 ? (
                                data.map((group) => (
                                    <Card
                                        key={group.title}
                                        elevation={0}
                                        onClick={() => {
                                            setSelectedTaskPage(1);
                                            setSelectedTaskGroup({
                                                title: group.title,
                                                count: group.count,
                                                tasks: []
                                            });
                                        }}
                                        sx={{
                                            minHeight: 58,
                                            px: 1.5,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            border: "1px solid #F0F1F4",
                                            borderRadius: "10px",
                                            backgroundColor: "#FAFAFB",
                                            cursor: "pointer",
                                            transition: "box-shadow 0.2s ease, transform 0.2s ease",
                                            "&:hover": {
                                                boxShadow: "0 3px 10px rgba(31, 41, 55, 0.08)",
                                                transform: "translateY(-1px)",
                                            },
                                        }}
                                    >
                                        <Stack direction="row" alignItems="center" spacing={1.2}>
                                            <Box
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    borderRadius: "9px",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    color: "#EF4444",
                                                    backgroundColor: "#FFF7ED",
                                                    "& svg": { fontSize: 19 },
                                                }}
                                            >
                                                {React.cloneElement(group.icon, { sx: { fontSize: 19 } })}
                                            </Box>
                                            <Typography sx={{ fontSize: 13, fontFamily: 'figtree', fontWeight: 700, color: "#1F2A63" }}>
                                                {group.title}
                                            </Typography>
                                        </Stack>
                                        <Stack direction="row" alignItems="center" spacing={0.8}>
                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>
                                                {group.count}
                                            </Typography>
                                            <Typography sx={{ fontSize: 12, color: "#6B7280" }}>
                                                {group.count === 1 ? "booking pending" : "bookings pending"}
                                            </Typography>
                                            <ChevronRight sx={{ fontSize: 19, color: "#9CA3AF" }} />
                                        </Stack>
                                    </Card>
                                ))
                            ) : (
                                <Typography sx={{ color: "#6B7280", fontSize: 12, fontFamily: 'figtree', textAlign: "center", py: 4 }}>
                                    No pending tasks found.
                                </Typography>
                            )
                        ) : data.length > 0 ? (
                            data.map((task, index) => (
                                <TaskItem
                                    key={task.bookingId || `${task.title}-${index}`}
                                    jobId={task.bookingId}
                                    title={task.title}
                                    subtitle={task.subtitle}
                                    icon={task.icon}
                                    iconBg={task.iconBg}
                                    iconColor={task.iconColor}
                                    due={task.due}
                                    priority={task.priority}
                                    priorityBg={task.priorityBg}
                                    priorityColor={task.priorityColor}
                                    tags={task.tags}
                                    isCritical={task.isCritical}
                                    bookingId={task?.bookingId}
                                    actionData={actionData}
                                    tab={selectedTab}
                                    role={role === "All" ? process.env.REACT_APP_ENTERPRISE_NAME_SENTENCECASE.toUpperCase() : role}
                                />
                            ))
                        ) : (
                            <Typography sx={{ color: "#6B7280", fontSize: 12, textAlign: "center", py: 4 }}>
                                No pending tasks found. 
                            </Typography>
                        )}
                    </Box>
                    {selectedTab === "Master List" && totalCount > 0 && (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ pt: 1.2 }}
                        >
                            <Typography sx={{ color: "#6B7280", fontSize: 11 }}>
                                Showing {(page - 1) * pageSize + 1} to {Math.min(page * pageSize, totalCount)} of {totalCount}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ color: "#6B7280", fontSize: 11 }}>Rows</Typography>
                                <Select
                                    size="small"
                                    value={pageSize}
                                    onChange={(event) => {
                                        setPageSize(Number(event.target.value));
                                        setPage(1);
                                    }}
                                    sx={{ height: 28, fontSize: 11, minWidth: 68 }}
                                >
                                    {[10, 20, 50].map((size) => (
                                        <MenuItem key={size} value={size}>{size}</MenuItem>
                                    ))}
                                </Select>
                                <Pagination
                                    page={page}
                                    count={Math.ceil(totalCount / pageSize)}
                                    onChange={(_, nextPage) => setPage(nextPage)}
                                    size="small"
                                    color="primary"
                                    siblingCount={0}
                                />
                            </Stack>
                        </Stack>
                    )}

                    {(selectedTab === "Group by Task" || selectedTab === "Group by Booking") && !selectedTaskGroup && groupTotalCount > 0 && (
                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={1}
                            sx={{ pt: 1.2 }}
                        >
                            <Typography sx={{ color: "#6B7280", fontSize: 11 }}>
                                Showing {(groupPage - 1) * groupPageSize + 1} to {Math.min(groupPage * groupPageSize, groupTotalCount)} of {groupTotalCount}
                            </Typography>
                            <Stack direction="row" alignItems="center" spacing={1}>
                                <Typography sx={{ color: "#6B7280", fontSize: 11 }}>Rows</Typography>
                                <Select
                                    size="small"
                                    value={groupPageSize}
                                    onChange={(event) => {
                                        setGroupPageSize(Number(event.target.value));
                                        setGroupPage(1);
                                    }}
                                    sx={{ height: 28, fontSize: 11, minWidth: 68 }}
                                >
                                    {[10, 20, 50].map((size) => (
                                        <MenuItem key={size} value={size}>{size}</MenuItem>
                                    ))}
                                </Select>
                                <Pagination
                                    page={groupPage}
                                    count={Math.ceil(groupTotalCount / groupPageSize)}
                                    onChange={(_, nextPage) => setGroupPage(nextPage)}
                                    size="small"
                                    color="primary"
                                    siblingCount={0}
                                />
                            </Stack>
                        </Stack>
                    )}
                </Box>
            </Box>
        </Box>
    );
}